import * as Sentry from '@sentry/browser';
import type { DeviceStatusEvent, DeviceStatusEventAPI, LastLocationAPI } from './definitions';
import { deviceStatusEventsTransformer } from './DeviceTransformer';
import { sendLatencyMetrics, sendRealtimeDelay } from './FleetEventMetrics';
import type { EventSourcePolyfill } from 'event-source-polyfill';
import type moment from 'moment';

import { Location, type UUID } from '@cobliteam/common-types';

import { DatadogTrack } from '@cobliteam/datadog-rum';

import Alert from '@cobliteam/ui/components/Alert';

import { EventSourceRequester } from '../../request/EventSourceRequester';
import type { Configuration } from '../../request/Types';

const DEFAULT_RETRY_TIMEOUT_IN_SECONDS = 3;
export class FleetEventSourceRequester {
  private eventSourceRequester: EventSourceRequester;

  private numberOfRetries = 0;

  constructor(configuration: Configuration & { useEventSourceLibrary: boolean }) {
    this.eventSourceRequester = new EventSourceRequester(configuration);
  }

  public subscribeToDeviceStatus = (fleetId: UUID, callback: (data: DeviceStatusEvent[]) => void, vehicleGroupIds?: UUID[]): Promise<EventSource | EventSourcePolyfill> => {
    const vehicleGroupIdsParameter = vehicleGroupIds && vehicleGroupIds.length > 0 ? `?wanted_vehicle_group_ids=${vehicleGroupIds.join(',')}` : '';

    const url = `/fleet-events/${fleetId}${vehicleGroupIdsParameter}`;
    let reconnectSSE: any;

    return this.eventSourceRequester.subscribe({ url }).then((eventSource) => {
      let lastTimestamp: moment.Moment | null = null;
      let counterEventsReceived = 0;

      eventSource.onerror = (err: Event) => {
        const ddError = new Error(`Connection failed, stack trace: <${err}>`);
        ddError.name = 'Fleet-events';
        DatadogTrack.error(ddError);

        eventSource.close();

        const currentLocation = window.location.href;
        const currentLocationIsMonitoring = currentLocation.includes(Location.MONITORING);

        if (currentLocationIsMonitoring) {
          reconnectSSE = setTimeout(
            () => {
              this.subscribeToDeviceStatus(fleetId, callback);
              this.numberOfRetries += 1;
            },
            DEFAULT_RETRY_TIMEOUT_IN_SECONDS ** this.numberOfRetries * 1000
          );
        }
      };

      eventSource.onopen = () => {
        DatadogTrack.action('Fleet-events connection open successfully');
        clearTimeout(reconnectSSE);
        this.numberOfRetries = 0;
      };

      // @ts-ignore
      eventSource.addEventListener('DeviceStatus[]', (evt: any) => {
        try {
          const parsedEventData: DeviceStatusEventAPI[] = JSON.parse(evt.data);
          const translatedEventData: LastLocationAPI[] = parsedEventData.map(({ speed_in_kmh, event_time, ...event }) => ({
            ...event,
            speed: speed_in_kmh,
            time: event_time,
            time_in_millis: event_time,
          }));

          const deviceStatusData: DeviceStatusEvent[] = deviceStatusEventsTransformer(translatedEventData);

          if (callback) {
            callback(deviceStatusData);
          }

          counterEventsReceived += 1;
          lastTimestamp = sendLatencyMetrics(lastTimestamp);
          sendRealtimeDelay(counterEventsReceived, translatedEventData);
        } catch (err) {
          Sentry.withScope((scope) => {
            scope.setTag('EventSource', 'DeviceStatus[]');
            Sentry.captureException(err);
          });

          console.error(err);
          Alert.error('Erro ao receber as informações de localização em tempo real');
        }
      });

      return eventSource;
    });
  };
}

export default FleetEventSourceRequester;
